import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants/routeNames';
import { Company } from '@/models/company';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

const Logistics = () => import('@/pages/Logistics/index.vue');
const LogisticsShipmentsView = () => import('@/pages/Logistics/Shipments/LogisticsShipments.vue');
const TrackOrdersView = () => import('@/pages/Logistics/TrackOrders/TrackOrders.vue');
const AssignOrdersView = () => import('@/pages/Logistics/AssignOrders/AssignOrders.vue');
const SplashPage = () => import('@/pages/Logistics/SplashPage.vue');
const DeliveriesOrdersView = () => import('@/pages/Logistics/Deliveries/DeliveriesOrders.vue');

async function beforeEnter() {
  await loadTranslations('logistics');
}

export function logisticsTabRoutes({ company }: { company?: Company }): Readonly<RouteTab[]> {
  const routes = [
    { to: { name: ROUTE_NAMES.TRACK_ORDERS }, label: i18n.t('logistics.tabs.manageOrders') },
    { to: { name: ROUTE_NAMES.LOGISTICS_SHIPMENTS }, label: i18n.t('logistics.tabs.servicedOrders') },
  ];

  if (!company?.isLogisticsOnly) {
    routes.splice(0, 0, { to: { name: ROUTE_NAMES.ASSIGN_ORDERS }, label: i18n.t('logistics.tabs.assignOrders') });
  }

  return Object.freeze(routes);
}

const logisticsRoutes: RouteRecordRaw[] = [
  {
    path: 'logistics/',
    component: Logistics,
    beforeEnter,
    meta: {
      tabs: logisticsTabRoutes,
      title: () => i18n.t('logistics.shippingDashboard'),
      description: () => i18n.t('logistics.shippingDashboardDescription'),
      permissions: [
        {
          rules: [
            ({ isSplitEnabled, permissions }) => {
              return Boolean(
                isSplitEnabled('enable_logistics_permission_to_all_users') || permissions?.canAccessShippingDashboard,
              );
            },
          ],
          redirect: (to) => ({ name: ROUTE_NAMES.LOGISTICS_LEARN_MORE, params: { company: to.params.company } }),
        },
        {
          rules: [
            'enableLogistics',
            'canAccessLogisticsDashboard',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId),
          ],
        },
      ],
    },
    children: [
      {
        path: '',
        name: ROUTE_NAMES.LOGISTICS,
        redirect: (to) => ({ name: ROUTE_NAMES.ASSIGN_ORDERS, params: { company: to.params.company } }),
      },
      {
        path: 'shipments',
        name: ROUTE_NAMES.LOGISTICS_SHIPMENTS,
        component: LogisticsShipmentsView,
        meta: { title: () => i18n.t('logistics.tabs.servicedOrders') },
      },
      {
        path: 'orders',
        name: ROUTE_NAMES.TRACK_ORDERS,
        component: TrackOrdersView,
        meta: {
          title: () => i18n.t('logistics.tabs.manageOrders'),
        },
      },
      {
        path: 'assign-orders',
        name: ROUTE_NAMES.ASSIGN_ORDERS,
        component: AssignOrdersView,
        meta: {
          title: () => i18n.t('logistics.tabs.assignOrders'),
          permissions: [
            {
              rules: ['canAccessAssignOrders'],
            },
          ],
        },
      },
    ],
  },
  {
    path: 'logistics/learn-more',
    name: ROUTE_NAMES.LOGISTICS_LEARN_MORE,
    component: SplashPage,
    beforeEnter,
    meta: {
      permissions: [{ rules: ['enableLogistics', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
    },
  },
  {
    path: 'logistics/deliveries',
    name: ROUTE_NAMES.LOGISTICS_DELIVERIES,
    component: DeliveriesOrdersView,
    beforeEnter,
    meta: {
      permissions: [
        {
          rules: ['canAccessDeliveries', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
        },
      ],
    },
  },
];

export default logisticsRoutes;
